import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Refund() {
  return (
    <div className="min-h-screen bg-bg text-ink" data-testid="refund-page">
      <header className="border-b border-line">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="text-sm font-bold inline-flex items-center gap-1.5 text-ink-soft hover:text-brand"><ArrowLeft className="w-4 h-4"/> Back</Link>
          <img src="/brand/petbond-logo.png" alt="PetBond" className="h-10"/>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-12 prose prose-sm">
        <h1 className="font-heading text-4xl font-extrabold">Refund Policy</h1>
        <p className="text-ink-soft text-sm">Last updated: {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>

        <p className="mt-4">At PetBond, we strive to deliver a great experience for every pet parent, vet, hostel, NGO and store on our platform. This policy explains when refunds are available and how to request one.</p>

        <h2 className="font-heading text-2xl font-extrabold mt-8">1. Premium subscriptions</h2>
        <ul>
          <li><b>Free trial</b> — Our Premium plans start with a free trial. No amount is charged during the trial period. You can cancel from <Link to="/app/billing" className="text-brand font-bold">Billing → Subscription</Link> anytime before renewal without any charge.</li>
          <li><b>Post-billing refund window</b> — If you were auto-charged for a renewal you did not intend, write to us within <b>7 days of the charge</b> at <a href="mailto:hello@petbond.in">hello@petbond.in</a>. We will process a full refund back to your original payment method within 5–7 business days.</li>
          <li><b>Pro-rata refunds</b> — For annual plans cancelled mid-cycle, a pro-rata refund of the unused months is issued on request, minus any Razorpay processing fee.</li>
          <li><b>No refund cases</b> — Refunds are not applicable after the 7-day window on monthly plans, or for accounts terminated for Terms-of-Service violations.</li>
        </ul>

        <h2 className="font-heading text-2xl font-extrabold mt-8">2. Vet consultations & appointments</h2>
        <ul>
          <li><b>Cancelled by user</b> — Free cancellation if done at least <b>2 hours before</b> the scheduled slot. Payments are refunded in 5–7 business days.</li>
          <li><b>Cancelled by vet</b> — 100% refund, always, credited within 5–7 business days.</li>
          <li><b>No-show by user</b> — No refund. The consultation fee is paid out to the vet as compensation for reserved time.</li>
          <li><b>Service quality issues</b> — Raise a dispute within 48 hours of the appointment at <a href="mailto:hello@petbond.in">hello@petbond.in</a>. We investigate with the vet and issue partial/full refunds based on findings.</li>
        </ul>

        <h2 className="font-heading text-2xl font-extrabold mt-8">3. Store orders (pet food, supplies)</h2>
        <ul>
          <li><b>Damaged / wrong item on delivery</b> — Report within 24 hours with photos. Full refund or replacement issued at your choice.</li>
          <li><b>Return / change of mind</b> — Unopened, unused items in original packaging can be returned within 7 days of delivery. Return shipping is at customer cost unless the seller is at fault.</li>
          <li><b>Perishable items</b> — Fresh food, treats and medicines are non-returnable unless damaged.</li>
        </ul>

        <h2 className="font-heading text-2xl font-extrabold mt-8">4. Hostel & daycare bookings</h2>
        <ul>
          <li><b>Cancelled more than 48 hours before check-in</b> — 100% refund.</li>
          <li><b>24–48 hours before check-in</b> — 50% refund.</li>
          <li><b>Less than 24 hours or no-show</b> — No refund.</li>
          <li><b>Cancelled by hostel</b> — 100% refund + assistance to find alternate boarding.</li>
        </ul>

        <h2 className="font-heading text-2xl font-extrabold mt-8">5. NGO donations</h2>
        <p>Donations routed to verified NGOs on PetBond are <b>final and non-refundable</b>. Please review the NGO profile carefully before donating. PetBond does not retain a platform fee from verified NGO donations — 100% goes to the NGO.</p>

        <h2 className="font-heading text-2xl font-extrabold mt-8">6. How to request a refund</h2>
        <ol>
          <li>Email <a href="mailto:hello@petbond.in">hello@petbond.in</a> or WhatsApp <a href="https://wa.me/919819793270" target="_blank" rel="noreferrer">+91 98197 93270</a></li>
          <li>Include your registered email, transaction ID, and reason for refund</li>
          <li>We acknowledge within 24 hours and process valid refunds within 5–7 business days</li>
          <li>Refunds are credited back to the original payment method (UPI, card, netbanking or wallet)</li>
        </ol>

        <h2 className="font-heading text-2xl font-extrabold mt-8">7. Payment gateway timelines</h2>
        <p>Once we initiate a refund, actual credit to your bank / UPI / card is subject to your bank's processing timelines — typically 3–7 business days for UPI/netbanking and 7–14 business days for credit cards. Refunds carry the Razorpay reference ID for tracking.</p>

        <h2 className="font-heading text-2xl font-extrabold mt-8">8. Contact</h2>
        <p>PetBond · Noida, Uttar Pradesh, India<br/>
        Email: <a href="mailto:hello@petbond.in">hello@petbond.in</a><br/>
        Phone/WhatsApp: <a href="tel:+919819793270">+91 98197 93270</a><br/>
        See also: <Link to="/cancellation" className="text-brand font-bold">Cancellation Policy</Link> · <Link to="/terms" className="text-brand font-bold">Terms of Service</Link></p>
      </main>
    </div>
  );
}
